import { LEGACY_WIDGET_TYPES, type MediaAssetRecord, type ScreenSummary } from '@codot/contracts';
import { t } from '../i18n';

/** Why `player` can't show `asset` (in a few words), or null when it can or hasn't said what it can do yet. */
export function cantPlay(asset: Pick<MediaAssetRecord, 'kind' | 'config'>, player: Pick<ScreenSummary, 'plays'>): string | null {
  const p = player.plays;
  if (!p) return null;
  if ((asset.kind === 'composition' || asset.kind === 'app') && !p.apps) return t('plays.noApps');
  if (asset.kind === 'pdf' && !p.pdf) return t('plays.noPdf');
  if (asset.kind === 'widget' && asset.config) {
    const known = p.widgets.length ? p.widgets : (LEGACY_WIDGET_TYPES as readonly string[]);
    if (!known.includes(asset.config.type)) return t('plays.noWidget');
  }
  return null;
}

/**
 * "Won't play on Lobby: it can't show compositions and custom apps" — for a card's flag and its tooltip, or null when
 * every one of `players` can show it.
 */
export function wontPlay(asset: Pick<MediaAssetRecord, 'kind' | 'config'>, players: Pick<ScreenSummary, 'name' | 'plays'>[]): string | null {
  const bad = players.flatMap((p) => {
    const why = cantPlay(asset, p);
    return why ? [{ name: p.name, why }] : [];
  });
  if (!bad.length) return null;
  const why = bad[0]!.why;
  if (players.length === 1) return t('plays.onOne', { name: bad[0]!.name, why });
  const names = bad.slice(0, 3).map((b) => b.name).join(', ') + (bad.length > 3 ? ` +${bad.length - 3}` : '');
  return t('plays.onSome', { n: bad.length, total: players.length, names, why });
}
